import React, { useState } from 'react';
import { Stack, Button, Typography, Box } from '@mui/material';
import { Add } from '@mui/icons-material';
import { ColumnType } from 'Components/Common/Tables/TableHeaders';
import TrimTables from 'Components/Reusable/TrimTables/TrimTables';
import GiftTypeTable, { giftTypeHead } from './GiftTypeTable';
import FilterListButton from 'Components/Reusable/FilterList/FilterList';
import BasicModal from 'Components/Reusable/Modals/BasicModal';
import ConfirmationModal from 'Components/Reusable/Modals/ConfirmationModal';
import CreateEditGiftType from 'Components/AdminPanel/GiftType/CreateEditGiftType';
import useGiftTypeActions, { GiftTypeData } from 'hooks/useGiftTypeActions';
import useUserActions from 'hooks/useUserActions';

const GiftType = () => {
	const { handleRoles } = useUserActions();
	const { deleteGiftType } = useGiftTypeActions();
	const [trimList, setTrimList] = useState<ColumnType[]>([]);
	const [filterPayload, setFilterPayload] = useState<Array<any>>([]);
	const [visible, setVisible] = useState(false);
	const [selectedData, setSelectedData] = useState<GiftTypeData>();
	const [isDelete, setIsDelete] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [reload, setReload] = useState(false);
	const [page, setPage] = useState(0);

	const handleCloseEdit = () => {
		setVisible(false);
		setSelectedData(undefined);
	};

	const handleOpenEdit = (data: GiftTypeData) => {
		setVisible(true);
		setSelectedData(data);
	};

	const handleOpenDelete = (data: GiftTypeData) => {
		setIsDelete(true);
		setSelectedData(data);
	};

	const handleCloseDelete = () => {
		setVisible(false);
		setSelectedData(undefined);
	};

	const handleCloseConfirmation = () => {
		setIsDelete(false);
	};

	const handleDelete = async () => {
		if (selectedData) {
			setDeleteLoading(true);
			const isSucess = await deleteGiftType(selectedData.id);
			setDeleteLoading(false);
			handleCloseDelete();
			handleCloseConfirmation();
			if (isSucess) {
				setReload(!reload);
			}
		}
	};

	return (
		<Stack>
			<Stack
				alignItems="center"
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent={'space-between'}
				spacing={1}
				mb={5}
			>
				<Stack direction="column">
					<Stack direction="row" spacing={4} alignItems="center">
						<Typography variant="h5" sx={{ color: 'var(--mainColor)' }}>
							Gift Types
						</Typography>
					</Stack>
					<Typography component={'span'} sx={{ color: '#7697ab', fontSize: 14, wordWrap: 'wrap' }}>
						Create and manage Gift Types here.
					</Typography>
				</Stack>
				<Stack sx={{ mb: 3 }} direction="row" alignItems={'center'} justifyContent="flex-end">
					<TrimTables name="giftTypeHead" list={giftTypeHead} trimList={trimList} setTrimList={setTrimList} />
					{handleRoles('admin-actions') && (
						<Button
							onClick={() => setVisible(true)}
							sx={{ textTransform: 'none', mr: 2 }}
							startIcon={<Add />}
							variant="contained"
						>
							Add Gift Type
						</Button>
					)}
					<FilterListButton
						setFilterPayload={setFilterPayload}
						filterPayload={filterPayload}
						setIsReload={setReload}
						reload={reload}
						setPage={setPage}
						page={page}
					/>
				</Stack>
			</Stack>
			<GiftTypeTable
				trimList={trimList}
				setTrimList={setTrimList}
				filterPayload={filterPayload}
				setFilterPayload={setFilterPayload}
				handleOpenEdit={handleOpenEdit}
				reload={reload}
				setReload={setReload}
				page={page}
				setPage={setPage}
			/>
			{visible && (
				<BasicModal
					onClose={handleCloseEdit}
					visible={visible}
					title={selectedData ? 'Edit Gift Type' : 'Add a Gift Type'}
					data={selectedData || undefined}
					handleOpenDelete={handleOpenDelete}
					isClose={false}
					noDelete={handleRoles('admin-actions')}
				>
					<CreateEditGiftType
						selectedEditData={selectedData}
						handleClose={handleCloseEdit}
						reload={reload}
						setReload={setReload}
					/>
				</BasicModal>
			)}
			{isDelete && (
				<ConfirmationModal
					handleClose={handleCloseConfirmation}
					message={'Are you sure you want to delete this gift type?'}
					open={isDelete}
					handleConfirmation={handleDelete}
					handleCancel={handleCloseConfirmation}
					loading={deleteLoading}
				/>
			)}
		</Stack>
	);
};

export default GiftType;
